/**
 * A MOLECULE, DRAWN.
 *
 * ── WHY OPENCHEMLIB AND NOT THE SMALLER ONE ──────────────────────────────────────────────────
 * Measured before choosing, because a structure goes into teacher content and the choice is hard
 * to take back. SmilesDrawer is 57K gzipped against OpenChemLib's 331K and loses anyway: it emits
 * an element only for a LABELLED atom — a skeletal carbon has no label, so no element — and
 * nothing at all per bond. That forecloses marking a group on a ring, and it forecloses the curly
 * arrows that come later, where one end of an arrow is very often a bond rather than an atom.
 *
 * OpenChemLib gives `m:Atom:0` and `m:Bond:0` for every one of them.
 *
 * ── THE SIZE IS REAL AND IS PAID ONLY WHEN SOMETHING NEEDS IT ────────────────────────────────
 * 331K gzipped is not nothing. It loads lazily and only when a structure is actually on screen,
 * so a reaction written entirely in mhchem — which is most of chemistry — never fetches it. The
 * app already lazy-loads MathLive at 220K the same way, so this is a familiar shape rather than
 * a new kind of cost.
 *
 * ── COLOUR HAS TO BE REMAPPED, AS KATEX'S DOES ───────────────────────────────────────────────
 * OpenChemLib bakes CPK colours as literal `rgb(…)`: black for carbon, red for oxygen, blue for
 * nitrogen. Left alone, a structure stays black-on-black in dark mode. So the SVG is rewritten to
 * theme tokens on the way out, which is exactly what `remapInkColors` does to KaTeX's output and
 * for the same reason — the renderer does not know about themes and should not have to.
 *
 * ── AND EVERY ONE OF THEM IS NOW THE BODY INK ────────────────────────────────────────────────
 * CPK is a real convention — Corey–Pauling–Koltun, the colours of the physical model kits, and
 * what PubChem and ChemDraw draw with — so the element hues were kept at first. They are gone
 * on the owner's call: *"i'm debating whether to keep the colors or just default everything to
 * black, as the colors draw your attention ... black it is ... the teacher can color them if
 * they want"*.
 *
 * Two reasons it is the right way round. Exam papers print structures in monochrome, so black
 * is the more faithful rendering of the thing a question is imitating. And colour cannot be
 * decoration and annotation at once: with nothing tinted by default, a colour on a drawing is
 * something the teacher put there, which is what makes highlighting work at all.
 *
 * `var(--ws-text)` rather than a literal black, so it inverts with the theme — which is what
 * carbon and the bonds already did, and why the sweep below was always the fallback.
 */

import { ALL_ELEMENTS } from './reaction-symbols';

import type { Molecule } from 'openchemlib';

export type StructureRender = {
  /** The SVG, themed, with every atom and bond addressable. */
  svg: string;
  /** Canonical SMILES, so the same molecule written two ways stores one way. */
  canonical: string;
  formula: string;
  error: null;
} | {
  svg: '';
  canonical: '';
  formula: '';
  /** What to show instead. A structure that will not parse is a typo, not a crash. */
  error: string;
};

/**
 * CPK, as OpenChemLib writes it, mapped onto ink the theme owns.
 *
 * The hues are kept — a chemist reads oxygen as red and nitrogen as blue, and recolouring them
 * would be a different notation — but they are taken from the palette so they invert with
 * everything else rather than staying lab-coat bright on a dark page.
 */
/**
 * HOW MANY PIXELS ONE OF THE DRAWING'S OWN UNITS IS WORTH.
 *
 * ── WHY A DRAWING NEEDS A SCALE AT ALL ───────────────────────────────────────────────────────
 * OpenChemLib draws every element symbol at a fixed `font-size="14"`, sized against the bonds
 * it drew, and we then crop the viewBox tight to the ink. Nothing in the SVG says how big it
 * should be, so a fixed CSS height decided it — and that scales a SMALL molecule up far more
 * than a large one. Measured in the workbench: ethanol came out at x4.06 and naphthalene at
 * x1.55, so ethanol's letters were 57px against naphthalene's 22 on the same panel, half the
 * height of its own drawing. Reported as *"the labels seem very big and they're off badly
 * aligned with the vertex they need to land on"* — a 57px glyph does not sit on a vertex, it
 * swallows it.
 *
 * So the drawing carries its own size, and the same unit is worth the same pixel everywhere:
 * bond lengths and letters match across molecules, ethanol draws small and naphthalene draws
 * large, which is how every chemistry tool behaves. The CSS caps it rather than setting it —
 * `max-height` and `max-width`, so a molecule too big for its box shrinks to fit and one that
 * fits is left alone.
 *
 * 1.3 puts the symbols at about 18px, which is the size they are on the panel this was tuned
 * against. It is the ONE number that decides how big every structure in the app looks.
 */
export const STRUCTURE_PX_PER_UNIT = 1.3;

/**
 * The drawing's natural size in pixels, written onto it.
 *
 * Needed as ATTRIBUTES rather than as CSS because an SVG carrying only a viewBox has no
 * intrinsic size, and `width: auto` then makes the browser derive one from whichever dimension
 * the CSS happened to fix — which is the behaviour this replaces.
 */
function withNaturalSize(svg: string): string {
  const m = /viewBox="([-\d.]+)\s+([-\d.]+)\s+([-\d.]+)\s+([-\d.]+)"/.exec(svg);
  if (!m) return svg;
  const w = Number(m[3]) * STRUCTURE_PX_PER_UNIT;
  const h = Number(m[4]) * STRUCTURE_PX_PER_UNIT;
  if (!(w > 0 && h > 0)) return svg;
  const sized = svg.replace(/^(<svg)([^>]*?)(\s*\/?>)/, (_all, open: string, attrs: string, close: string) =>
    `${open}${attrs.replace(/\s(?:width|height)="[^"]*"/g, '')}`
    + ` width="${w.toFixed(1)}" height="${h.toFixed(1)}"${close}`);
  return sized;
}

/** Every CPK hue the library bakes in goes to the body ink — see the head of this file. */
const LEFTOVER_RGB = /rgb\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*\)/g;

export function themeStructureSvg(svg: string): string {
  return svg.replace(LEFTOVER_RGB, 'var(--ws-text)');
}

/**
 * The whole drawing in one ink, for a structure a teacher has highlighted.
 *
 * CPK is thrown away deliberately here. A highlight says "look at this one", and a molecule that
 * keeps its red oxygens while turning purple says two things at once — the teacher cannot tell
 * which colour they chose and which the library did. Every OTHER structure keeps its CPK, so the
 * highlighted one is the one that differs, which is the whole job.
 */
export function tintStructureSvg(svg: string, colour: string): string {
  return svg.replace(LEFTOVER_RGB, colour);
}

/**
 * The library, fetched once and shared.
 *
 * A module-level promise rather than a per-call import so that ten structures in one task fetch
 * one copy. `null` until something asks, which is what keeps it off the critical path.
 */
let loading: Promise<typeof import('openchemlib')> | null = null;

export function loadChemistry(): Promise<typeof import('openchemlib')> {
  loading ??= import('openchemlib');
  return loading;
}

/** True once the library is in memory, so a caller can render synchronously without a flash. */
export function chemistryReady(): boolean {
  return cached !== null;
}

let cached: typeof import('openchemlib') | null = null;

export async function prepareChemistry(): Promise<void> {
  const mod = await loadChemistry();
  cached = (mod as { default?: typeof import('openchemlib') }).default ?? mod;
}

/**
 * Draw a SMILES string, once the library is in memory.
 *
 * Synchronous on purpose: the render paths that need this — a Tiptap node view, the stored-HTML
 * pass — are synchronous themselves, so the loading is hoisted to `prepareChemistry` and this
 * stays a pure function of its input. Called before the library is ready it reports that rather
 * than throwing, and the caller re-renders when the promise settles.
 */
export function renderStructure(
  smiles: string,
  opts?: { width?: number; height?: number; id?: string; colour?: string; crop?: boolean },
): StructureRender {
  const src = smiles.trim();
  if (!src) return { svg: '', canonical: '', formula: '', error: 'empty' };
  if (!cached) return { svg: '', canonical: '', formula: '', error: 'loading' };

  const width = opts?.width ?? 180;
  const height = opts?.height ?? 120;
  const id = opts?.id ?? 'struct';

  try {
    const mol: Molecule = cached.Molecule.fromSmiles(src);
    if (!mol.getAllAtoms()) return { svg: '', canonical: '', formula: '', error: 'empty' };
    /* CROPPED to the molecule. Without it every drawing is a 180x120 box with the structure
       small in the middle, so a benzene ring set inline in a reaction carried half a line of
       white space on each side and could not be told apart from the gap beside it. */
    /* Cropped by default, because inline in a reaction the box's white space reads as a gap.
       The arrow picker asks for the FULL canvas instead: a cropped drawing is only as big as
       the molecule, and stretching that to fill a panel blows the index pins up with it. */
    /*
     * NO STEREO DESCRIPTORS ON THE DRAWING. Four separate flags, because they are four things.
     *
     * `suppressChiralText` alone — which is all we had — only removes the molecule-level caption
     * ("this enantiomer", "unknown chirality"). It leaves a label stamped beside every
     * stereocentre, and those labels are what the owner reported as unreadable: alanine drew as
     * `RabsN2HOHO`, glucose as `OHRabsO?OHRabsOHSabsOHSabsOH`. They are three different things
     * run together at 5px —
     *
     *   - `R` / `S`   the CIP descriptor for that centre     → `suppressCIPParity`
     *   - `abs`       OpenChemLib's ESR group: this is the
     *                 ABSOLUTE configuration rather than a
     *                 racemate (`&1`) or an unknown one (`or1`)  → `suppressESR`
     *   - `?`         a stereocentre with no configuration given → also `suppressCIPParity`
     *   - `Z` / `E`   the same descriptor on a double bond       → also `suppressCIPParity`
     *
     * All four go, and none of the chemistry goes with them: the configuration is still DRAWN,
     * as wedge and hash bonds and as the geometry of a double bond, which is how a textbook
     * shows it. The descriptor is carried by the molecule's NAME — `(2S)-2-aminopropanoic acid`,
     * `(Z)-but-2-ene` — which is also the half a screen reader gets. `abs` is an authoring
     * artefact that means nothing to a teacher, and glucose's `?` is worse than noise: it marks
     * the anomeric carbon, which is CORRECTLY undefined, and reads as a fault in the drawing.
     *
     * To put R/S back for an organic-chemistry context, drop `suppressCIPParity` — but read
     * `ownerOf` in `structure-overlay.ts` first: it assumes every `<text>` here is an atom's
     * label, and that assumption is only true because of these flags.
     */
    const svg = mol.toSVG(width, height, id, {
      suppressChiralText: true,
      suppressESR: true,
      suppressCIPParity: true,
      autoCrop: opts?.crop !== false,
      autoCropMargin: 2,
    });
    return {
      svg: withNaturalSize(opts?.colour ? tintStructureSvg(svg, opts.colour) : themeStructureSvg(svg)),
      canonical: mol.toSmiles(),
      formula: mol.getMolecularFormula().formula,
      error: null,
    };
  } catch (e) {
    /* A structure that will not parse is a typo. The caller shows the SMILES instead, the same
       way an unrenderable mhchem token shows its own source rather than disappearing. */
    return { svg: '', canonical: '', formula: '', error: String((e as Error)?.message ?? e) };
  }
}

/** The id of an atom or bond inside a rendered structure, for marking and later for arrows. */
export const atomId = (id: string, index: number) => `${id}:Atom:${index}`;
export const bondId = (id: string, index: number) => `${id}:Bond:${index}`;

/**
 * WHAT EACH ATOM AND BOND IS, for naming an arrow's ends.
 *
 * "Atom 2" says nothing: a teacher reading a menu cannot tell carbon from oxygen, and once a
 * drawing holds two species — which is what an intermolecular arrow needs — they cannot tell
 * which molecule it belongs to either. The owner said exactly that. Both facts are in the
 * molecule and neither is in the number.
 *
 * `part` is the fragment index, counted from 1 and only meaningful when there is more than one.
 */
export type AnchorFacts = {
  atoms: { label: string; part: number }[];
  bonds: { label: string; part: number }[];
  parts: number;
};

export function anchorFacts(smiles: string): AnchorFacts {
  const empty: AnchorFacts = { atoms: [], bonds: [], parts: 0 };
  if (!cached || !smiles.trim()) return empty;
  try {
    const mol = cached.Molecule.fromSmiles(smiles.trim());
    const fragments: number[] = [];
    const parts = mol.getFragmentNumbers(fragments, false, false);
    const atoms = Array.from({ length: mol.getAllAtoms() }, (_, i) => ({
      label: mol.getAtomLabel(i),
      part: (fragments[i] ?? 0) + 1,
    }));
    const bonds = Array.from({ length: mol.getAllBonds() }, (_, i) => {
      const a = mol.getBondAtom(0, i);
      const b = mol.getBondAtom(1, i);
      return {
        /* The two atoms it joins, which is how a chemist would point at a bond. */
        label: `${mol.getAtomLabel(a)}\u2013${mol.getAtomLabel(b)}`,
        part: (fragments[a] ?? 0) + 1,
      };
    });
    return { atoms, bonds, parts };
  } catch {
    return empty;
  }
}

/**
 * WHAT SMILES LETS YOU WRITE, as the panel prints it and as the warning checks it.
 *
 * Ten elements may be written bare; six of those have a lower-case form meaning aromatic;
 * everything else in the periodic table goes in square brackets. Probed against the renderer
 * rather than taken from the specification, because what the panel claims has to be what this
 * build actually accepts: `Si`, `Na`, `Fe`, `Mg`, `Se`, `H`, `K` and `Li` are all REJECTED bare,
 * and `[Au]`, `[13C]` and `[OH-]` are all fine.
 */
export const BARE_ELEMENTS = ['B', 'C', 'N', 'O', 'P', 'S', 'F', 'Cl', 'Br', 'I'] as const;

/** The six with a lower-case form. Lower case means aromatic, not a different bond. */
export const AROMATIC_ELEMENTS = ['b', 'c', 'n', 'o', 'p', 's'] as const;

/**
 * THE ONE WAY A SMILES CAN BE WRONG AND STILL DRAW.
 *
 * `Pb` is not lead. It is phosphorus followed by an aromatic boron, and it parses, and it draws
 * two atoms, and nothing anywhere says so — the teacher gets a plausible picture of the wrong
 * molecule. Thirteen real elements do this, every one of them a symbol whose first letter is in
 * the bare set and whose second is in the aromatic set:
 *
 *   Sc Co Nb In Sn Sb Cs Os Pb Po Np No Cn
 *
 * DERIVED rather than listed, from the same 118-entry table the element keys are built from, so
 * the warning cannot fall behind the periodic table. The count is pinned in the tests.
 *
 * Bracket contents are cut first: `[Pb]` is lead and must not be warned about, which is the
 * whole of the advice this gives.
 */
const SPLITS = new Map<string, string>(
  ALL_ELEMENTS
    .filter(e => e.length === 2
      && (BARE_ELEMENTS as readonly string[]).includes(e.slice(0, 1))
      && (AROMATIC_ELEMENTS as readonly string[]).includes(e.slice(1, 2)))
    .map(e => [e, `${e.slice(0, 1)} + ${e.slice(1, 2).toUpperCase()}`]),
);

/** The first real element in `smiles` that is silently being read as two atoms, if any. */
export function splitElement(smiles: string): { symbol: string; as: string } | null {
  /* Outside brackets only, and the cut keeps the string's length so nothing shifts under the
     scan — a bracket is replaced by spaces rather than removed. */
  const bare = smiles.replace(/\[[^\]]*\]?/g, m => ' '.repeat(m.length));
  for (let i = 0; i < bare.length - 1; i++) {
    const pair = bare.slice(i, i + 2);
    const as = SPLITS.get(pair);
    if (as) return { symbol: pair, as };
  }
  return null;
}

/**
 * A FORMULA SPLIT INTO WHAT IS SET SMALL AND WHAT IS NOT.
 *
 * OpenChemLib hands back `C6H6`, a flat string, and a flat string is not a formula — a teacher
 * reading `C6H6` where the drawing says benzene is reading a part number. Every digit run in it
 * is a count, because the library's formula carries no charge and no isotope (probed: `[OH-]`
 * comes back `HO`, `[13C]` comes back `C`), so the rule is the whole rule and there is no
 * superscript case to get wrong.
 */
export function formulaParts(formula: string): { text: string; sub: boolean }[] {
  return formula.split(/(\d+)/).filter(Boolean).map(text => ({ text, sub: /^\d+$/.test(text) }));
}
